// FormikDropdown.tsx – MUI Autocomplete ile arama
import React from 'react';
import { useField } from 'formik';
import { Autocomplete, TextField, FormHelperText } from '@mui/material';

interface Option {
  value: string | number;
  label: string;
}

interface FormikDropdownProps {
  name: string;
  options: Option[];
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: (value: string | number) => void;
}

export const FormikDropdown: React.FC<FormikDropdownProps> = ({
  name,
  options,
  label,
  placeholder,
  className,
  disabled,
  required,
  onChange,
}) => {
  const [field, meta, helpers] = useField(name);

  const selectId = `${name}-select`;
  const selectedOption = options.find(o => o.value === field.value) ?? null;

  const handleChange = (_: React.SyntheticEvent, newValue: Option | null) => {
    const value = newValue?.value ?? '';
    helpers.setValue(value);
    onChange?.(value);
  };

  return (
    <div className={className}>
      <Autocomplete
        id={selectId}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onBlur={() => helpers.setTouched(true)}
        disabled={disabled}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        slotProps={{
          listbox: {
            sx: {
              '& .MuiAutocomplete-option': {
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-of-type': {
                  borderBottom: 'none',
                },
              },
            },
          },
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={meta.touched && Boolean(meta.error)}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
            }}
          />
        )}
      />
      {meta.touched && meta.error && (
        <FormHelperText error>{meta.error}</FormHelperText>
      )}
    </div>
  );
};
